import { useEffect, useState, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Plus, Car, Download, Search } from 'lucide-react'
import { DataTable } from '@/components/common/DataTable'
import { CreateVehicleDialog } from '@/components/vehicles/CreateVehicleDialog'
import { toast } from 'sonner'
import { VehicleService } from '@/services/vehicleService'
import { DriverMasterService } from '@/services/driverMasterService'
import { Vehicle } from '@/types/vehicle'
import { DriverMaster } from '@/types/driverMaster'
import {
  createVehicleColumns,
  getVisibleVehicleColumns,
  DEFAULT_VEHICLE_COLUMNS,
  type VehicleActions
} from '@/components/reports/reportConfig'
import { ExportDialog } from '@/components/common/dialogs/ExportDialog'
import { ColumnVisibilityControl } from '@/components/ui/ColumnVisibilityControl'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'

export function VehiclesPage() {
  const { currentUser } = useAuth()
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [drivers, setDrivers] = useState<DriverMaster[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [creating, setCreating] = useState(false)
  const [assigningDrivers, setAssigningDrivers] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [showExportDialog, setShowExportDialog] = useState(false)

  // Column visibility
  const { columns: columnConfigs, visibleColumns, updateColumnVisibility, resetToDefaults } = useColumnVisibility({
    storageKey: 'vehicleColumns',
    defaultColumns: DEFAULT_VEHICLE_COLUMNS
  })

  // Load data
  useEffect(() => {
    if (!currentUser) return

    const loadData = async () => {
      try {
        setLoading(true)
        const [vehiclesData, driversData] = await Promise.all([
          VehicleService.getVehicles(currentUser),
          DriverMasterService.getDrivers(currentUser)
        ])
        setVehicles(vehiclesData)
        setDrivers(driversData)
      } catch (err) {
        console.error('Fel vid laddning av data:', err)
        setError('Det gick inte att ladda data')
        toast.error('Det gick inte att ladda data')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [currentUser])

  // Handle create vehicle
  const handleCreateVehicle = async (registrationNumber: string) => {
    if (!currentUser) {
      toast.error('Användare inte inloggad')
      return
    }

    try {
      setCreating(true)
      const newVehicle = await VehicleService.createVehicle(currentUser, {
        carRegNumber: registrationNumber
      })
      setVehicles(prev => [...prev, newVehicle])
      setShowCreateDialog(false)
      toast.success('Fordon skapat')
    } catch (err) {
      console.error('Fel vid skapande av fordon:', err)
      const errorMessage = err instanceof Error ? err.message : 'Det gick inte att skapa fordonet'
      toast.error(errorMessage)
      throw err // Re-throw for dialog error handling
    } finally {
      setCreating(false)
    }
  }

  // Handle delete vehicle
  const handleDeleteVehicle = async (vehicle: Vehicle) => {
    if (!currentUser) return

    try {
      await VehicleService.deleteVehicle(currentUser, vehicle.id)

      // Optimistically update UI
      setVehicles(prev => prev.filter(v => v.id !== vehicle.id))
      setDrivers(prev => prev.map(driver =>
        driver.carId === vehicle.id
          ? { ...driver, carId: undefined }
          : driver
      ))

      toast.success('Fordon borttaget')
    } catch (err) {
      console.error('Fel vid borttagning av fordon:', err)
      const errorMessage = err instanceof Error ? err.message : 'Det gick inte att ta bort fordonet'
      toast.error(errorMessage)

      // Reload data on error to sync with backend
      const [vehiclesData, driversData] = await Promise.all([
        VehicleService.getVehicles(currentUser),
        DriverMasterService.getDrivers(currentUser)
      ])
      setVehicles(vehiclesData)
      setDrivers(driversData)
    }
  }

  // Handle driver assignment to vehicle
  const handleAssignDriver = async (vehicleId: string, driverId: string | null) => {
    if (!currentUser) return

    // Add vehicle to loading state
    setAssigningDrivers(prev => new Set(prev).add(vehicleId))

    // Store original state for rollback
    const originalDrivers = [...drivers]

    try {
      // Optimistically update UI first
      const updatedDrivers = drivers.map(driver => {
        // Clear vehicle from any other drivers
        if (driver.carId === vehicleId) {
          return { ...driver, carId: undefined }
        }
        // Assign vehicle to selected driver
        if (driver.id === driverId) {
          return { ...driver, carId: vehicleId }
        }
        return driver
      })
      setDrivers(updatedDrivers)

      // Use atomic transaction for backend update
      await DriverMasterService.assignVehicleToDriver(currentUser, vehicleId, driverId)

      toast.success(driverId ? 'Förare tilldelad fordon' : 'Förare borttagen från fordon')
    } catch (err) {
      console.error('Fel vid tilldelning av förare:', err)
      const errorMessage = err instanceof Error ? err.message : 'Det gick inte att tilldela föraren'
      toast.error(errorMessage)

      // Rollback optimistic update
      setDrivers(originalDrivers)
    } finally {
      // Remove vehicle from loading state
      setAssigningDrivers(prev => {
        const newSet = new Set(prev)
        newSet.delete(vehicleId)
        return newSet
      })
    }
  }

  // Get driver assigned to vehicle
  const getAssignedDriver = (vehicleId: string): DriverMaster | null => {
    return drivers.find(driver => driver.carId === vehicleId) || null
  }

  // Filter vehicles based on search
  const filteredVehicles = useMemo(() => {
    if (!searchTerm) return vehicles
    return vehicles.filter(vehicle =>
      vehicle.carRegNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getAssignedDriver(vehicle.id)?.primaryName.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [vehicles, searchTerm, drivers])

  // Vehicle actions for DataTable
  const vehicleActions: VehicleActions = {
    onDeleteVehicle: handleDeleteVehicle,
    onAssignDriver: handleAssignDriver,
    getAssignedDriver,
    drivers,
    assigningDrivers
  }

  // Create columns
  const columns = useMemo(() => {
    const visibleColumnKeys = visibleColumns.map(col => col.key)
    return getVisibleVehicleColumns(visibleColumnKeys, vehicleActions)
  }, [visibleColumns, vehicleActions])

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Car className="h-12 w-12 mx-auto mb-4 text-muted-foreground animate-spin" />
          <p className="text-muted-foreground">Laddar fordon...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>Försök igen</Button>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Fordon</h1>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Lägg till Fordon
            </Button>
            <Button onClick={() => setShowExportDialog(true)} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Exportera
            </Button>
          </div>
        </div>

        <Separator />

        {/* Table Controls */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 justify-between">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Sök fordon eller förare..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ColumnVisibilityControl
              columns={columnConfigs}
              onVisibilityChange={updateColumnVisibility}
              onResetToDefaults={resetToDefaults}
            />
          </div>
        </div>

        {/* Table */}
        {filteredVehicles.length === 0 ? (
          <div className="text-center py-12 border rounded-lg">
            <Car className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium mb-2">Inga fordon registrerade</p>
            <p className="text-muted-foreground mb-4">
              Lägg till ditt första fordon för att komma igång med fordonshantering.
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Lägg till Fordon
            </Button>
          </div>
        ) : (
          <DataTable
            data={filteredVehicles}
            columns={columns}
          />
        )}
      </div>

      {/* Create Vehicle Dialog */}
      <CreateVehicleDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreateVehicle}
        loading={creating}
        existingRegistrations={vehicles.map(v => v.carRegNumber)}
      />

      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        data={filteredVehicles}
        columns={columns}
        baseFilename="vehicles_report"
        title="Exportera fordonslista"
      />
    </>
  )
}